<template>
  <a-modal
    :open="visible"
    :title="`批量操作 — ${nodeIds.length} 个节点`"
    :confirm-loading="submitting"
    ok-text="确认执行"
    cancel-text="取消"
    :ok-button-props="{ disabled: !canSubmit, danger: action === 'stop' }"
    @ok="onSubmit"
    @cancel="onCancel"
  >
    <a-alert
      type="warning"
      show-icon
      style="margin-bottom:16px"
    >
      <template #message>
        <span v-if="!action">
          请先明确选择要执行的操作，未选择时不会向节点发送任何命令。
        </span>
        <span v-else-if="action === 'stop'">
          将停止 {{ nodeIds.length }} 个 agent 节点 — 所有活跃 SOCKS5 玩家连接都会断！
        </span>
        <span v-else-if="action === 'restart'">
          将重启 {{ nodeIds.length }} 个 agent — 短暂停服（KillMode=process 会保留 child，玩家断连几秒）。
        </span>
        <span v-else>
          将升级 {{ nodeIds.length }} 个 agent 到 admin 当前二进制版本。offline 节点会被跳过。
        </span>
      </template>
    </a-alert>

    <a-space>
      <a-radio-group v-model:value="action" :disabled="submitting">
        <a-radio-button value="restart">重启</a-radio-button>
        <a-radio-button value="upgrade">升级</a-radio-button>
        <a-radio-button value="stop">停止</a-radio-button>
      </a-radio-group>
    </a-space>

    <a-divider />

    <div v-if="submitting || result" data-test="batch-action-result">
      <a-spin :spinning="submitting">
        <div v-if="result" data-test="batch-action-summary">
          <a-statistic
            title="已派发"
            :value="result.dispatched"
            :value-style="{ color: '#52c41a' }"
            style="display:inline-block;margin-right:32px"
          />
          <a-statistic
            title="已跳过"
            :value="result.skipped"
            :value-style="{ color: '#faad14' }"
            style="display:inline-block;margin-right:32px"
          />
          <a-statistic
            v-if="result.ws_failed"
            title="WS 失败"
            :value="result.ws_failed"
            :value-style="{ color: '#f5222d' }"
            style="display:inline-block"
          />
        </div>
        <a-table
          v-if="result && result.results.length"
          :columns="resultColumns"
          :data-source="result.results"
          :pagination="false"
          size="small"
          row-key="node_id"
          style="margin-top:12px"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'status'">
              <a-tag :color="statusColor(record.status)">{{ statusLabel(record.status) }}</a-tag>
            </template>
            <template v-else-if="column.key === 'node_name'">
              <span>{{ record.node_name || `#${record.node_id}` }}</span>
            </template>
            <template v-else-if="column.key === 'acked'">
              <span v-if="record.acked === true" style="color:#52c41a">已 ack</span>
              <span v-else-if="record.acked === false" style="color:#faad14">未 ack</span>
              <span v-else style="color:#9ca3af">—</span>
            </template>
            <template v-else-if="column.key === 'reason'">
              <span v-if="record.reason" style="color:#9ca3af">{{ record.reason }}</span>
              <span v-else style="color:#9ca3af">—</span>
            </template>
          </template>
        </a-table>
      </a-spin>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { message } from 'ant-design-vue';
import {
  batchControlNode,
  type BatchNodeControlResponse,
} from '../api/nodes';
import type { NodeItem } from '../types';
import { confirmForceRun, runGuarded } from '../utils/maintenanceForce';

type ActionKind = 'restart' | 'upgrade' | 'stop';

const props = defineProps<{
  visible: boolean;
  nodeIds: number[];
  // nodes are passed in so we can show node_name next to the id
  // in the result table (the backend already returns node_name, but
  // we surface it client-side too for instant feedback before the
  // request resolves).
  nodes: NodeItem[];
}>();
const emit = defineEmits<{
  (e: 'update:visible', v: boolean): void;
  (e: 'success', payload: { dispatched: number; skipped: number; ws_failed: number }): void;
}>();

const action = ref<ActionKind | null>(null);
const submitting = ref(false);
const result = ref<BatchNodeControlResponse | null>(null);

// Soft cap mirrors the backend's 50. We hard-refuse locally so the
// operator gets an immediate error instead of a 400.
const BATCH_LIMIT = 50;

const canSubmit = computed(() => {
  if (submitting.value) return false;
  if (props.nodeIds.length === 0) return false;
  if (props.nodeIds.length > BATCH_LIMIT) return false;
  return action.value !== null;
});

const resultColumns = [
  { title: '节点', dataIndex: 'node_name', key: 'node_name', width: 180 },
  { title: 'ID', dataIndex: 'node_id', key: 'node_id', width: 70 },
  { title: '状态', dataIndex: 'status', key: 'status', width: 100 },
  { title: 'ack', dataIndex: 'acked', key: 'acked', width: 80 },
  { title: '原因', dataIndex: 'reason', key: 'reason' },
];

const statusColor = (s: string) => {
  switch (s) {
    case 'dispatched': return 'green';
    case 'skipped': return 'orange';
    case 'ws_failed': return 'red';
    default: return 'default';
  }
};
const statusLabel = (s: string) => {
  switch (s) {
    case 'dispatched': return '已派发';
    case 'skipped': return '已跳过';
    case 'ws_failed': return 'WS 失败';
    default: return s;
  }
};

watch(
  () => props.visible,
  (v) => {
    if (v) {
      // Never preselect a node control action. A stale/default choice can
      // restart production nodes when the operator intended to upgrade.
      action.value = null;
      result.value = null;
      submitting.value = false;
    }
  },
);

const onCancel = () => {
  if (submitting.value) return;
  emit('update:visible', false);
};

const onSubmit = async () => {
  if (!canSubmit.value || !action.value) return;
  submitting.value = true;
  result.value = null;
  try {
    const chosen = action.value;
    const outcome = await runGuarded((force) => batchControlNode(props.nodeIds, chosen, force), confirmForceRun);
    if (!outcome) return;
    const r = outcome.result;
    result.value = r.data;
    emit('success', {
      dispatched: r.data.dispatched,
      skipped: r.data.skipped,
      ws_failed: r.data.ws_failed,
    });
  } catch (e: any) {
    const errCode = e?.response?.data?.error?.code;
    const msg = e?.response?.data?.error?.message || e?.message || '批量操作失败';
    if (errCode === 'INVALID_INPUT') {
      message.error(`参数错误: ${msg}`);
    } else {
      message.error(msg);
    }
  } finally {
    submitting.value = false;
  }
};
</script>
